/*
 * animations.js — Motion One enhancement layer for She Formal.
 *
 * The HYBRID layer: everything that can be expressed in pure CSS/WAAPI lives in
 * animations.css (no dependency). This module is reserved for the two surfaces
 * where a real spring genuinely earns its keep — the homepage slideshow and the
 * Instagram reel carousel — using Motion One (motion.dev), the vanilla-JS sibling
 * of Framer Motion.
 *
 * Principles:
 *   - Animate transform / opacity only (compositor-friendly).
 *   - Tasteful + sparse: one purposeful spring per surface, never micro-spam.
 *   - Gated behind a JS reduced-motion check (mirrors the CSS gate). Users who
 *     prefer reduced motion get the static, fully-painted storefront untouched.
 *   - Defensive: every hook bails on a missing element. No section is required
 *     to exist; absence is a no-op, never an error.
 */

import { animate, inView, spring } from 'motion';

const prefersReducedMotion =
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Pointer is a real mouse/trackpad — gate desktop-only affordances. */
const hasFinePointer =
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(any-pointer: fine)').matches;

/**
 * Spring entrance on the slideshow.
 *
 * Hooking the theme's internal slide-change events is fragile (the slideshow is
 * a custom element with its own scroll-snap scroller), so we take the robust
 * path described in the brief: a single subtle spring entrance on the slideshow
 * container the first time it scrolls into view. Opacity + scale only.
 */
const initSlideshow = () => {
  const slideshow = document.querySelector('slideshow-component');
  if (!slideshow) return;

  // Start from the rested visual state so a failed/absent animation never hides content.
  inView(
    slideshow,
    (element) => {
      animate(
        element,
        { opacity: [0, 1], transform: ['scale(0.985)', 'scale(1)'] },
        { type: spring, stiffness: 220, damping: 30, mass: 1 }
      );
      // Run once.
      return undefined;
    },
    { amount: 0.25 }
  );
};

/**
 * Reel carousel affordance.
 *
 * Selectors per brief: track `[data-reel-track]`, items `.instagram-shoppable__reel`.
 *   - Spring on scroll-into-view for each reel item (gentle stagger by index).
 *   - Desktop only: a soft pointer-drag "peek" that nudges the track and springs
 *     back, hinting the carousel is draggable. Mobile keeps its native
 *     scroll-snap untouched (we never touch overflow / scroll behaviour).
 */
const initReels = () => {
  const track = document.querySelector('[data-reel-track]');
  if (!track) return;

  const reels = track.querySelectorAll('.instagram-shoppable__reel');
  if (reels.length) {
    reels.forEach((reel, index) => {
      inView(
        reel,
        (element) => {
          animate(
            element,
            { opacity: [0, 1], transform: ['translateY(24px)', 'translateY(0px)'] },
            { type: spring, stiffness: 260, damping: 28, delay: Math.min(index, 6) * 0.05 }
          );
          return undefined;
        },
        { amount: 0.4 }
      );
    });
  }

  // Desktop-only drag affordance. Native mobile scroll-snap is left alone.
  if (!hasFinePointer) return;

  let startX = 0;
  let dragging = false;

  const onPointerDown = (event) => {
    if (event.pointerType !== 'mouse') return;
    dragging = true;
    startX = event.clientX;
  };

  const onPointerMove = (event) => {
    if (!dragging) return;
    // Damped follow — a hint, not a full drag-scroll takeover.
    const delta = (event.clientX - startX) * 0.12;
    track.style.transform = `translateX(${Math.max(Math.min(delta, 28), -28)}px)`;
  };

  const release = () => {
    if (!dragging) return;
    dragging = false;
    animate(
      track,
      { transform: 'translateX(0px)' },
      { type: spring, stiffness: 300, damping: 24 }
    );
  };

  track.addEventListener('pointerdown', onPointerDown);
  track.addEventListener('pointermove', onPointerMove);
  track.addEventListener('pointerup', release);
  track.addEventListener('pointerleave', release);
  track.addEventListener('pointercancel', release);
};

const init = () => {
  initSlideshow();
  initReels();
};

// Reduced motion: skip the enhancement layer entirely. CSS already paints the
// rested state, so there is nothing to do.
if (!prefersReducedMotion) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
}
